import dayjs from 'dayjs'
import { CommentDataType } from '../../shared/components/Comments/Comments.types'
import { getCommentsDashboardTabIdByDataType } from '../../shared/components/Comments/Comments.helpers'
import {
    CommentsViewComment,
    CommentsViewCommonFilterDefinition,
    CommentsViewDateRangeFiltersState,
    CommentsViewDomainFilterDefinition,
    CommentsViewDomainTabId,
    CommentsViewFilterOption,
    CommentsViewTabId,
} from './CommentsView.types'

export const RESOLVED_FILTER_OPTIONS: CommentsViewFilterOption[] = [
    { id: 'resolved', label: 'Resolved', order: 0 },
    { id: 'unresolved', label: 'Unresolved', order: 1 },
]

type GetCommentsDashboardCommonFilterDefinitionsArgs = {
    getTypeLabel: (dataType: CommentDataType) => string
}

type GetCommentsDashboardDomainFilterDefinitionsArgs = {
    getActivityStatusLabel: (statusId: string) => string
    getActivityStatusOrder: (statusId: string) => number
}

const getUniqueOptions = (options: CommentsViewFilterOption[]) => {
    const seen = new Set<string>()

    return options
        .filter((option) => {
            if (seen.has(option.id)) {
                return false
            }

            seen.add(option.id)

            return true
        })
        .sort((left, right) => {
            const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER
            const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER

            if (leftOrder !== rightOrder) {
                return leftOrder - rightOrder
            }

            return left.label.localeCompare(right.label)
        })
}

export const getSelectedFilterOptionIds = (options: CommentsViewFilterOption[], excludedOptionIds: string[] = []) => {
    const excludedOptionIdsSet = new Set(excludedOptionIds)

    return options.map((option) => option.id).filter((optionId) => !excludedOptionIdsSet.has(optionId))
}

export const getExcludedFilterOptionIds = (options: CommentsViewFilterOption[], selectedOptionIds: string[] = []) => {
    const selectedOptionIdsSet = new Set(selectedOptionIds)

    return options.map((option) => option.id).filter((optionId) => !selectedOptionIdsSet.has(optionId))
}

export const getCommentsDashboardDomainTabId = (
    comment: Pick<CommentsViewComment, 'dataType'> & Partial<CommentsViewComment>
) => {
    return comment.location?.domain ?? getCommentsDashboardTabIdByDataType(comment.dataType)
}

export const getCommentsDashboardDomainComments = (
    comments: CommentsViewComment[],
    domainTabId: CommentsViewDomainTabId
) => comments.filter((comment) => getCommentsDashboardDomainTabId(comment) === domainTabId)

export const getCommentsDashboardCommonFilterDefinitions = ({
    getTypeLabel,
}: GetCommentsDashboardCommonFilterDefinitionsArgs): CommentsViewCommonFilterDefinition[] => [
    {
        id: 'isResolved',
        label: 'Resolved',
        placeholder: 'states',
        getOptions: () => RESOLVED_FILTER_OPTIONS,
        matches: (comment, selectedOptionIds) =>
            (comment.isResolved && selectedOptionIds.includes('resolved')) ||
            (!comment.isResolved && selectedOptionIds.includes('unresolved')),
    },
    {
        id: 'user',
        label: 'User',
        placeholder: 'users',
        getOptions: (comments) =>
            getUniqueOptions(
                comments
                    .map((comment) =>
                        comment.user?._id && comment.user?.name
                            ? {
                                  id: comment.user._id,
                                  label: comment.user.name,
                              }
                            : null
                    )
                    .filter((option): option is CommentsViewFilterOption => Boolean(option))
            ),
        matches: (comment, selectedOptionIds) => !!comment.user?._id && selectedOptionIds.includes(comment.user._id),
    },
    {
        id: 'type',
        label: 'Type',
        placeholder: 'types',
        getOptions: (comments) =>
            getUniqueOptions(
                comments.map((comment) => {
                    const typeLabel = getTypeLabel(comment.dataType)

                    return {
                        id: typeLabel,
                        label: typeLabel,
                    }
                })
            ),
        matches: (comment, selectedOptionIds) => selectedOptionIds.includes(getTypeLabel(comment.dataType)),
    },
]

export const getCommentsDashboardDomainFilterDefinitions = ({
    getActivityStatusLabel,
    getActivityStatusOrder,
}: GetCommentsDashboardDomainFilterDefinitionsArgs): Partial<
    Record<CommentsViewDomainTabId, CommentsViewDomainFilterDefinition[]>
> => ({
    valueMessage: [
        {
            id: 'category',
            label: 'Category',
            placeholder: 'categories',
            multiple: true,
            getOptions: (comments) =>
                getUniqueOptions(
                    comments
                        .map((comment) => comment.extras?.category)
                        .filter((category): category is string => Boolean(category))
                        .map((category) => ({
                            id: category,
                            label: category,
                        }))
                ),
            matches: (comment, selectedOptionIds) => {
                const category = comment.extras?.category

                return !category || selectedOptionIds.includes(category)
            },
        },
    ],
    activity: [
        {
            id: 'status',
            label: 'Status',
            placeholder: 'statuses',
            multiple: true,
            getOptions: (comments) =>
                getUniqueOptions(
                    comments
                        .map((comment) => comment.extras?.status)
                        .filter((status): status is string => Boolean(status))
                        .map((status) => ({
                            id: status,
                            label: getActivityStatusLabel(status),
                            order: getActivityStatusOrder(status),
                        }))
                ),
            matches: (comment, selectedOptionIds) => {
                const status = comment.extras?.status

                return !status || selectedOptionIds.includes(status)
            },
        },
    ],
})

export const applyCommentsViewFilters = ({
    comments,
    activeTabId,
    dateRange,
    commonFilters,
    domainFiltersByTabId,
}: {
    comments: CommentsViewComment[]
    activeTabId: CommentsViewTabId
    dateRange: CommentsViewDateRangeFiltersState
    commonFilters: Array<{
        definition: CommentsViewCommonFilterDefinition
        selectedOptionIds: string[]
    }>
    domainFiltersByTabId: Partial<
        Record<
            CommentsViewDomainTabId,
            Array<{
                definition: CommentsViewDomainFilterDefinition
                selectedOptionIds: string[]
            }>
        >
    >
}) =>
    comments.filter((comment) => {
        const matchesCommonFilters = commonFilters.every(({ definition, selectedOptionIds }) =>
            definition.matches(comment, selectedOptionIds)
        )

        if (!matchesCommonFilters) {
            return false
        }

        const createdAt = dayjs(comment.createdAt)

        if (dateRange.from && createdAt.isBefore(dayjs(dateRange.from).startOf('day'))) {
            return false
        }

        if (dateRange.to && createdAt.isAfter(dayjs(dateRange.to).endOf('day'))) {
            return false
        }

        const domainTabId = getCommentsDashboardDomainTabId(comment)

        if (!domainTabId) {
            return activeTabId === 'all'
        }

        if (activeTabId !== 'all' && activeTabId !== domainTabId) {
            return false
        }

        return (domainFiltersByTabId[domainTabId] ?? []).every(({ definition, selectedOptionIds }) =>
            definition.matches(comment, selectedOptionIds)
        )
    })
